import { useEffect, useRef, useState } from 'react'
import type { CourseWithoutBlocks } from '@/types/course'
import {
  courseFieldIssues,
  lessonFieldIssues,
  type BlockIssue,
} from '@/features/editor/lib/publishIssues'
import { useHasStartedTranslationRun } from '../studio/useBlocksInRun'
import { ClientIssuesGate, clientOnlyIssues, type ClientIssuesState } from './ClientIssuesGate'
import { PublishIssuesTable } from '../EditorHeader/PublishIssuesTable'
import type { ViolationGo } from '../publish/violationPlace'

interface Props {
  course: CourseWithoutBlocks
  /** Черновик формы — поля курса считаем по нему, как и подсказку селекта. */
  local: CourseWithoutBlocks
  /** Блоки уроков проверяем после задержки (`CourseAccessBlock` — очередь запросов). */
  ready: boolean
  onGo: (target: ViolationGo) => void
  /** Итог клиентских проверок (переполнение): `pending` — замеры ещё идут или не начались. */
  onClientIssues?: (state: { pending: boolean; issues: BlockIssue[] }) => void
}

/**
 * Вкладка «Доступ»: тот же список «что поправить перед публикацией», что в
 * облачке у «Опубликовать», — таблицами, с переходом к месту. Пусто — ничего.
 */
export const PublishIssuesPanel = ({ course, local, ready, onGo, onClientIssues }: Props) => {
  const startedRun = useHasStartedTranslationRun(course.id)
  const [gate, setGate] = useState<ClientIssuesState>({ pending: true, byLesson: {} })
  const lessons = course.lessons ?? []
  const fields = courseFieldIssues(local, startedRun)
  const lessonIssues = lessonFieldIssues(course, startedRun)
  const blocks = lessons.flatMap((l) => gate.byLesson[l.id] ?? [])
  const hasIssues = fields.length > 0 || lessonIssues.length > 0 || blocks.length > 0
  const { pending } = gate
  const clientIssues = clientOnlyIssues(course, gate.byLesson)
  // Докладываем, только когда итог изменился: иначе новая ссылка на массив гоняла бы родителя по кругу.
  // Место (урок, экран) в подписи — перестановка экранов обновляет текст отказа.
  const signature = `${pending}|${clientIssues
    .map((i) => `${i.lessonId}:${i.blockId}:${i.lang}:${i.lesson}:${i.screen}`)
    .join(',')}`
  const reported = useRef<string | null>(null)
  useEffect(() => {
    if (reported.current === signature) return
    reported.current = signature
    onClientIssues?.({ pending, issues: clientIssues })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- состав итога целиком в signature
  }, [signature])
  return (
    <>
      <ClientIssuesGate course={course} ready={ready} onChange={setGate} />
      {hasIssues ? (
        <PublishIssuesTable
          fields={fields}
          lessons={lessonIssues}
          blocks={blocks}
          tone="surface"
          onGo={onGo}
        />
      ) : null}
    </>
  )
}
